import { TechlineStatusCode as TechlineStatusCodeDto } from '@packages/clients';

import type { AllowCaseFeatures } from '../types';

import { TechlineStatusCode } from '../types';

/**
 * Translates granular case status values from the API into meta status for business logic
 * evaluation. For example, both "IN_REVIEW" and "ESCALATED" map to the meta status "SUBMITTED".
 */
export const caseStatusMap: Record<string, TechlineStatusCode[]> = {
    /* Cases that have not yet been submitted or reviewed */
    DRAFT: [TechlineStatusCode.DRAFT],

    /* Cases that are currently under review */
    SUBMITTED: [
        TechlineStatusCode.ESCALATED,
        TechlineStatusCode.IN_REVIEW,
        TechlineStatusCode.PENDING_TECHLINE,
        TechlineStatusCode.UPDATE_REQUESTED,
    ],

    /* Cases that have been closed */
    CLOSED: [TechlineStatusCode.AUTO_CLOSED, TechlineStatusCode.CLOSE_RESOLVED, TechlineStatusCode.CLOSED],
};

/**
 * Maps each official Techline status to the set of legacy and NorthStar DTO values that should
 * normalize to it. This is the human-readable "source of truth" for the mapping; the inverted
 * lookup map (`dtoToOfficialStatus`) is derived from it at module load.
 *
 * @see {@link https://subaruofamerica.atlassian.net/browse/TECHSHARE-5526 TECHSHARE-5526} for the
 *   full status migration table.
 */
export const statusNormalizationMap: Record<TechlineStatusCode, TechlineStatusCodeDto[]> = {
    [TechlineStatusCode.AUTO_CLOSED]: [TechlineStatusCodeDto.CLOSED_AUTO_CLOSED],
    [TechlineStatusCode.CLOSE_RESOLVED]: [
        TechlineStatusCodeDto.CLOSED_RESOLVED,
        TechlineStatusCodeDto.NS_CLOSED_APPROVED,
        TechlineStatusCodeDto.NS_CLOSED_REFERRED,
        TechlineStatusCodeDto.NS_CLOSED_RESOLUTION_UNKNOWN,
        TechlineStatusCodeDto.NS_TECHSHARE_SOFT_CLOSE,
    ],
    [TechlineStatusCode.CLOSED]: [
        TechlineStatusCodeDto.NS_CLOSED_CREATED_IN_ERROR,
        TechlineStatusCodeDto.NS_CLOSED_DUPLICATE,
        TechlineStatusCodeDto.NS_CLOSED_REJECTED,
    ],
    [TechlineStatusCode.DRAFT]: [],
    [TechlineStatusCode.ESCALATED]: [TechlineStatusCodeDto.LEGACY_OPEN_ESCALATED],
    [TechlineStatusCode.IN_REVIEW]: [TechlineStatusCodeDto.LEGACY_OPEN],
    [TechlineStatusCode.PENDING_TECHLINE]: [
        TechlineStatusCodeDto.LEGACY_PENDING_TECHLINE,
        TechlineStatusCodeDto.NS_DEALER_UPDATE,
        TechlineStatusCodeDto.NS_PENDING_CM,
        TechlineStatusCodeDto.NS_PENDING_PART_DELAY,
        TechlineStatusCodeDto.NS_PENDING_RESEARCH,
    ],
    [TechlineStatusCode.UPDATE_REQUESTED]: [TechlineStatusCodeDto.NS_PENDING_DEALER_UPDATE],
};

/** Default flags for case features. CID/CVN cases override these defaults to false. */
export const DEFAULT_ALLOW_CASE_FEATURES: AllowCaseFeatures = {
    allowAdditionalWorksheets: true,
    allowCloseCase: true,
    allowCustomerConcernDetails: true,
    allowDtcCodes: true,
    allowFailCodes: true,
    allowTechDescription: true,
};

/** All non-closed Techline statuses. Used when fetching only active cases. */
export const OPEN_CASE_STATUSES: TechlineStatusCode[] = [...caseStatusMap.DRAFT, ...caseStatusMap.SUBMITTED];
